import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  Input,
  OnChanges,
  Optional,
  Renderer2,
  ViewEncapsulation,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { PlIconRegistry } from './pl-icon-registry.service';
import { PlIcon } from '../icons';
import { ComponentChanges } from '@app/utils/simpleChangesType';

export enum IconFrame {
  none = '!p-0 border-none',
  square = 'grid border-solid',
  round = 'grid border-solid rounded-full',
}

export enum IconBorderWidth {
  small = 'border-2',
  big = 'border-[5px]',
}

@Component({
  selector: 'pl-icon-private',
  template: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      pl-icon-private {
        display: block;
        height: 100%;
        width: 100%;
      }

      pl-icon-private svg {
        height: 100%;
        width: 100%;
      }
    `,
  ],
})
export class PlIconPrivateComponent {
  private svgIcon!: SVGElement;

  @Input() public alt: string = '';

  @Input({ required: true })
  public set name(iconName: PlIcon) {
    if (this.svgIcon) {
      this.element.nativeElement.removeChild(this.svgIcon);
    }
    let svgData: string = this.plIconRegistry.getIcon(iconName);

    if (svgData) {
      svgData = svgData.replace('{{title}}', this.alt);
      this.svgIcon = this.svgElementFromString(svgData);
      this.render.setAttribute(this.element.nativeElement, 'name', iconName);

      this.element.nativeElement.appendChild(this.svgIcon);
    }
  }

  constructor(
    private element: ElementRef,
    private plIconRegistry: PlIconRegistry,
    private render: Renderer2,
    @Optional() @Inject(DOCUMENT) private document: any,
  ) {}

  private svgElementFromString(svgContent: string): SVGElement {
    const div: HTMLElement = this.document.createElement('DIV');
    div.innerHTML = svgContent;
    return div.querySelector('svg') || this.document.createElementNS('http://www.w3.org/2000/svg', 'path');
  }
}

@Component({
  selector: 'pl-icon',
  template: `
    <div [ngClass]="[frame, borderWidth, additionalCssClassIcon]" class="h-full w-full">
      <pl-icon-private [ngClass]="[additionalCssClassIconPrivate]" [name]="name" />
    </div>
  `,
})
export class PlIconComponent implements OnChanges {
  @Input() public frame: IconFrame = IconFrame.none;
  @Input() public borderWidth: IconBorderWidth = IconBorderWidth.small;
  @Input({ required: true }) public name!: PlIcon;

  @HostBinding('class.rounded-full') public get rounded(): boolean {
    return this.frame === IconFrame.round;
  }

  public additionalCssClassIcon: string = '';
  public additionalCssClassIconPrivate: string = '';

  constructor(private element: ElementRef) {}

  public ngOnChanges(changes: ComponentChanges<PlIconComponent>): void {
    if (changes.name || changes.frame) {
      this.computeAdditionalClasses();
    }
  }

  private computeAdditionalClasses(): void {
    this.additionalCssClassIcon = '';
    this.additionalCssClassIconPrivate = '';

    if (this.frame !== IconFrame.none) {
      if ([PlIcon.pdf].includes(this.name)) {
        this.additionalCssClassIcon += ' p-[22%]';
        this.additionalCssClassIconPrivate += ' -translate-y-[10%]';
      } else if ([PlIcon.lock, PlIcon.pen_to_square].includes(this.name)) {
        this.additionalCssClassIcon += ' p-[18%]';
        this.additionalCssClassIconPrivate += ' -translate-y-[10%]';
      } else {
        this.additionalCssClassIcon += ' p-[10%]';
      }

      if ([PlIcon.chevron_forward].includes(this.name)) {
        this.additionalCssClassIconPrivate += ' translate-x-px';
      } else if ([PlIcon.chevron_back].includes(this.name)) {
        this.additionalCssClassIconPrivate += ' -translate-x-px';
      }
    }

    const allClassNameOnHost: string[] = this.element.nativeElement.className.split(' ');
    if (allClassNameOnHost.find((className: string) => className.startsWith('border-'))) {
      this.additionalCssClassIcon += ' border-inherit';
    } else {
      this.additionalCssClassIcon += ' border-current';
    }
  }
}
